/**
 * Host-account seam — how a HOST tells the shared app tree who is signed in.
 *
 * Same pattern as the Router choice and isElectron(): the shared tree stays
 * identical across surfaces, the host decides identity. The desktop renderer
 * provides NOTHING (loopback, no accounts) → default null → the AccountMenu
 * renders nothing and desktop stays pixel-identical. The cloud host
 * (apps/cloud-web CloudAuthGate) provides the live Better Auth session.
 *
 * `lastSyncedAt` is optional by design: it renders only when the host actually
 * has a sync record (the desktop↔cloud sync slice) — never a fabricated value.
 */
import { createContext, useContext } from 'react';

export interface HostAccount {
  /** Display name (from the auth provider profile). */
  name: string;
  /** Account email — the primary "who am I" anchor shown to the user. */
  email: string;
  /** Avatar URL (e.g. the GitHub profile image). Falls back to initials. */
  image?: string | null;
  /** ISO timestamp of the last completed desktop↔cloud sync, when known. */
  lastSyncedAt?: string | null;
  /** Ends the session. The host owns the redirect/gate flip that follows. */
  signOut: () => Promise<void>;
  /**
   * Deletes the account and everything stored with it (hosts whose accounts can be deleted).
   * Resolves 'deleted', or 'reauth' when the provider first needs a recent sign-in; rejects on any
   * other failure. The host owns what follows a deletion (back to its sign-in screen).
   */
  deleteAccount?: () => Promise<'deleted' | 'reauth'>;
}

export const HostAccountContext = createContext<HostAccount | null>(null);

/** Null on hosts without accounts (desktop) — callers must render nothing then. */
export function useHostAccount(): HostAccount | null {
  return useContext(HostAccountContext);
}
